import React from 'react';
import styled from 'styled-components';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { SignupModalMultiBrands, Button, useScreenQuery } from 'playup-web-core';
import dayjs from 'dayjs';

import appConfig from '../../../appConfig';
import { defaultFormData } from './defaultFormData';
import validationSchema from './validationSchema';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';
import useGoogleTagManager from '../../../hooks/useGoogleTagManager';
import FormSection from '../FormSection';
import ControlledPhoneNumberInput from './ControlledPhoneNumberInput';
import ControlledTextInputRevealPassword from '../../../components/Input/ControlledTextInputRevealPassword';
import ControlledAddressInput, { Title } from '../../../components/PortalGeoAddress/ControlledAddressInput';
import ControlledBirthDateInput from '../../../components/PortalGeoAddress/ControlledBirthDateInput';
import Agreements from './Agreements';
import USAgreements from './USAgreements';
import SignUpFooter from './SignUpFooter';
import ControlledInput from '../../../components/PortalGeoAddress/ControlledInput';
import ControlledSelectInput from '../../../components/PortalGeoAddress/ControlledSelectInput';
import ApprovedOperators from './ApprovedOperators';
import FinancialProviders from './FinancialProviders/FinancialProviders';

const isUS = appConfig.regions.isUS;
const fantasyEnabled = appConfig.fantasy.enabled;

const SignUpModal = ({ smallScreen = false, showRG, onClose }: SignUpModalProps) => {
	const { user, signUpUser, login } = React.useContext(PortalSessionContext);
	const history = useHistory();
	const { startRegistration, completeRegistration } = useGoogleTagManager();

	const [confirmationMode, setConfirmationMode] = React.useState<boolean>(false);
	const [usTermsAndConditionsAllChecked, setUSTermsAndConditionsAllChecked] = React.useState<boolean>(false);

	const formMethods = useForm<SignupFormData>({
		defaultValues: defaultFormData,
		mode: 'all',
		resolver: yupResolver(validationSchema),
	});
	const { handleSubmit, setError, formState, setFocus } = formMethods;
	const { isSubmitting } = formState;
	const { isSmall: isSmallScreen } = useScreenQuery();
	const [extraError, setExtraError] = React.useState<string>();
	// Useful for development, prefill the form with a random user
	// React.useEffect(() => {
	// 	randomUserGenerator().then(reset);
	// }, [reset]);

	// Redirect user after a successful registration and login
	React.useEffect(() => {
		if (user?.id) {
			if (user?.id_verified) {
				history.replace('?modal=onboarding-deposit', { from: 'registration' });
				return;
			} else {
				history.replace('?modal=onboarding-verification', { from: 'registration' });
				return;
			}
		}
	}, [history, user?.id, user?.id_verified]);

	React.useEffect(() => {
		startRegistration();
	}, [formState.isDirty, startRegistration]);
	const onComfirm = React.useCallback(() => {
		setConfirmationMode(false);
	}, []);
	const onSubmit: SubmitHandler<SignupFormData> = React.useCallback(
		async (formData) => {
			if (isUS && !confirmationMode) {
				setConfirmationMode(true);
				return;
			}
			const {
				set_deposit_limit,
				allowPromotionalMaterial,
				deposit_limit,
				isManualAddress,
				address,
				opted_in,
				...rest
			} = formData;
			if (set_deposit_limit && deposit_limit?.limit) {
				deposit_limit.limit *= 100;
			}
			let addressData: {
				city?: string;
				postcode?: string;
				state?: string;
				street_name?: string;
				street_number?: string;
				street_type?: string;
			} = {
				city: isManualAddress ? formData.city : address?.city,
				postcode: isManualAddress ? formData.postcode : address?.postcode,
				state: isManualAddress ? formData.state : address?.state,
				street_name: isManualAddress ? formData.street_name : address?.street_name,
				street_number: isManualAddress ? formData.street_number : address?.street_number,
				street_type: isManualAddress ? formData.street_type : address?.street_type,
			};
			try {
				await signUpUser({
					...rest,
					...(set_deposit_limit && { deposit_limit: deposit_limit }),
					...addressData,
					birth_date: dayjs(formData.birth_date).format('YYYY-MM-DD'),
					accept_terms: true,
					opted_out: !opted_in,
				});

				completeRegistration();
				await login(formData.email, formData.password);
			} catch (e) {
				//@ts-ignore
				const apiErrors = e?.errors as APIError;
				if (apiErrors) {
					for (const [key, value] of Object.entries(apiErrors)) {
						const errorMessage = value.join('. ');
						if (key === 'error') {
							setExtraError(apiErrors['error'].join('. \n'));
						} else {
							setError(key as any, { type: 'apiError', message: errorMessage });
						}
					}
					if (Object.keys(apiErrors)[0] !== 'error') {
						setFocus(Object.keys(apiErrors)[0] as any);
					}
				}
				if (isUS) setConfirmationMode(false);
			}
		},
		[completeRegistration, confirmationMode, login, setError, setFocus, signUpUser]
	);
	return (
		<SignupModalMultiBrands
			title={
				confirmationMode ? 'CONFIRM DETAILS' : fantasyEnabled ? 'CREATE A SINGLE ACCOUNT TO BET AND PLAY' : 'SIGN UP'
			}
			onClose={onClose}
		>
			<FormProvider {...formMethods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<FormSection>
						<StyledInput
							name="invite_code"
							label="Code (Optional)"
							type="text"
							placeholder="Enter a welcome code"
							hasBottomMargin={false}
							disabled={confirmationMode}
						/>
					</FormSection>
					<FormSection title={'CREATE YOUR LOGIN DETAILS'}>
						<StyledInput
							name="name"
							label="Username"
							type="text"
							placeholder="Enter your username"
							disabled={confirmationMode}
						/>
						<StyledInput
							name="email"
							label="Email"
							type="email"
							placeholder="Enter your email"
							disabled={confirmationMode}
						/>
						<ControlledTextInputRevealPassword
							textAlign="left"
							style={{ marginBottom: '5px' }}
							fieldName="password"
							label="Password"
							type="password"
							autoComplete="new-password"
							placeholder="Type a new password"
							disabled={confirmationMode}
						/>
					</FormSection>
					<FormSection title={'VERIFY WHO YOU ARE'}>
						<StyledInput
							name="first_name"
							label="First Name"
							type="text"
							placeholder="Must match your ID"
							disabled={confirmationMode}
						/>
						<StyledInput
							name="last_name"
							label="Last Name"
							type="text"
							placeholder="Must match your ID"
							disabled={confirmationMode}
						/>
						<ControlledBirthDateInput disabled={confirmationMode} />
						{isUS ? (
							<StyledInput
								name="phone"
								label="Cell Number"
								type="text"
								autoComplete="tel"
								placeholder="Enter your cell number"
								disabled={confirmationMode}
							/>
						) : (
							<ControlledPhoneNumberInput isSmallScreen={smallScreen} />
						)}
						{isUS && (
							<StyledInput
								name="ssn"
								label="Last 4 digits of SSN *"
								type="password"
								placeholder="0000"
								disabled={confirmationMode}
							/>
						)}
						<Title isSmall={isSmallScreen}>Country</Title>
						<ControlledSelectInput
							className=" _mb-5"
							options={
								isUS
									? [
											{
												label: 'United States of America',
												value: 'US',
											},
									  ]
									: [
											{
												label: 'Australia',
												value: 'AU',
											},
											{
												label: 'New Zealand',
												value: 'NZ',
											},
									  ]
							}
							placeholder="Country"
							name="country"
						/>
						<ControlledAddressInput />
						<Border />

						{isUS ? (
							<USAgreements
								confirmationMode={confirmationMode}
								setUSTermsAndConditionsAllChecked={setUSTermsAndConditionsAllChecked}
							/>
						) : (
							<Agreements isSmallScreen={smallScreen} />
						)}
						{extraError && <p style={{ color: 'red' }}>{extraError}</p>}
						<StyledButton
							kind="white"
							type="submit"
							height={50}
							isLoading={isSubmitting}
							disabled={isUS && !usTermsAndConditionsAllChecked}
						>
							{confirmationMode ? 'Confirm' : 'Create Account'}
						</StyledButton>
						{confirmationMode && (
							<StyledButton kind="white" height={50} onClick={onComfirm}>
								Edit details
							</StyledButton>
						)}
						<SignUpFooter showRG={showRG} />
					</FormSection>
				</form>
			</FormProvider>
			<ApprovedOperators />
			<FinancialProviders />
		</SignupModalMultiBrands>
	);
};
export default SignUpModal;

interface SignUpModalProps {
	smallScreen?: boolean;
	showRG?: boolean;
	onClose: () => void;
}

const StyledButton = styled(Button)({
	margin: '8px auto',
});

const Border = styled.div(({ theme }) => ({
	width: '90px',
	margin: '20px auto 10px',
	borderBottom: '1px solid',
	borderColor: theme.colors.lightGreyR,
}));

const StyledInput = styled(ControlledInput)<{ hasBottomMargin?: boolean }>(({ hasBottomMargin = true }) => ({
	marginBottom: hasBottomMargin ? '20px' : 0,
}));
